import { useState } from "react";
import { Loader2, Lock, Globe } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { errMsg, SITUATION_LABELS, useApi } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import {
  EducationSection, PortfolioSection, SituationSection, SkillsSection, StudySection, TargetSection, WorkSection, cleanProfile,
} from "@/components/learner/ProfileSections";

const Card = ({ title, children }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
    <h2 className="mb-5 text-lg font-semibold text-slate-900">{title}</h2>
    {children}
  </section>
);

export default function Profile() {
  const api = useApi();
  const { me, refresh } = useMe();
  const lp = me.learner_profile;
  const [p, setP] = useState({ ...lp, portfolio_links: lp.portfolio_links || [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { user_id, onboarded, created_at, updated_at, ...rest } = cleanProfile(p);
      await api.put("/me/learner-profile", rest);
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleVisibility = async (v) => {
    try {
      await api.patch("/me/visibility", { is_public: v });
      set("is_public", v);
      await refresh();
      toast.success(v ? "Profile is now public to approved employers" : "Profile is now private");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader eyebrow={SITUATION_LABELS[lp.situation]} title="Your profile">
        <Button onClick={save} disabled={busy} className="rounded-full bg-[#222d99] px-6 hover:bg-[#1a237e]" data-testid="profile-save-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
        </Button>
      </PageHeader>
      <div className="space-y-4">
        <div className={`flex items-center justify-between gap-4 rounded-2xl border p-5 ${p.is_public ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
          <div className="flex gap-3">
            {p.is_public ? <Globe className="mt-0.5 h-5 w-5 text-emerald-700" /> : <Lock className="mt-0.5 h-5 w-5 text-slate-500" />}
            <div>
              <p className="font-semibold text-slate-900" data-testid="profile-visibility-label">{p.is_public ? "Public profile" : "Private profile"}</p>
              <p className="text-sm text-slate-600">When public, approved employers can find you. Your contact details stay hidden until you accept their interest.</p>
            </div>
          </div>
          <Switch checked={p.is_public} onCheckedChange={toggleVisibility} data-testid="profile-public-toggle" aria-label="Public profile" />
        </div>
        <Card title="Current situation"><SituationSection value={p.situation} onChange={(v) => set("situation", v)} /></Card>
        <Card title="Goals"><TargetSection p={p} set={set} /></Card>
        <Card title="Skills"><SkillsSection skills={p.skills} onChange={(v) => set("skills", v)} /></Card>
        <Card title="Education"><EducationSection items={p.education} onChange={(v) => set("education", v)} /></Card>
        <Card title="Experience"><WorkSection items={p.work_history} onChange={(v) => set("work_history", v)} /></Card>
        <Card title="Portfolio links"><PortfolioSection links={p.portfolio_links} onChange={(v) => set("portfolio_links", v)} /></Card>
        <Card title="Learning preferences"><StudySection p={p} set={set} /></Card>
      </div>
    </div>
  );
}
